import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './AddressForm.css';

// ============ TypeScript Types ============

export type Language = string;

// TODO: Re-enable additional countries when multi-region shipping is supported
// NOTE: CheckoutForm.tsx uses a subset ('US' | 'KR' | 'CA') — consolidate when re-enabling
export type CountryCode = 'KR' | 'US' | 'JP' | 'CN' | 'GB' | 'OTHER';

export interface AddressData {
  recipientName: string;
  phone: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  country: CountryCode;
}

export interface AddressFormProps {
  initialAddress?: Partial<AddressData>;
  onSave: (data: AddressData) => Promise<void> | void;
  onCancel: () => void;
  isOpen?: boolean;
  language?: Language;
  forSampleRequest?: boolean;
}

type FormErrors = Partial<Record<keyof AddressData, string>>;

// English-only pattern for US address fields (letters, numbers, common punctuation)
const ENGLISH_ONLY_PATTERN = /^[A-Za-z0-9\s.,#\-'/()]+$/;

// ============ Daum Postcode API (disabled — US-only for now) ============
// TODO: Re-enable when Korean shipping is supported
// interface DaumPostcodeData {
//   zonecode: string;
//   roadAddress: string;
//   jibunAddress: string;
//   sido: string;
//   sigungu: string;
// }
//
// interface DaumPostcode {
//   open: () => void;
// }
//
// interface DaumPostcodeConstructor {
//   new (config: { oncomplete: (data: DaumPostcodeData) => void }): DaumPostcode;
// }
//
// declare global {
//   interface Window {
//     daum?: {
//       Postcode: DaumPostcodeConstructor;
//     };
//   }
// }

// ============ Component ============

const AddressForm: React.FC<AddressFormProps> = ({
  initialAddress = {},
  onSave,
  onCancel,
  isOpen = true,
  language = 'ko',
  forSampleRequest = false
}) => {
  // ============ State ============
  const [formData, setFormData] = useState<AddressData>({
    recipientName: initialAddress.recipientName || '',
    phone: initialAddress.phone || '',
    address1: initialAddress.address1 || '',
    address2: initialAddress.address2 || '',
    city: initialAddress.city || '',
    state: initialAddress.state || '',
    postalCode: initialAddress.postalCode || '',
    // Force US regardless of initialAddress — US-only shipping for now
    country: 'US'
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // ============ Translation Hook ============
  const { t } = useTranslation('checkout');
  // language prop kept for backward compatibility (Daum API uses Korean interface)

  // ============ Update form when initialAddress changes ============
  useEffect(() => {
    if (initialAddress) {
      setFormData(prev => ({
        ...prev,
        ...initialAddress,
        // Force US regardless of initialAddress — US-only shipping for now
        country: 'US'
      }));
    }
  }, [initialAddress]);

  // ============ Form Validation ============
  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    // Recipient name validation
    if (!formData.recipientName.trim()) {
      newErrors.recipientName = t('addressForm.recipientNameRequired');
    }

    // Phone validation
    if (!formData.phone.trim()) {
      newErrors.phone = t('addressForm.phoneRequired');
    } else if (!/^[\d\-+() ]+$/.test(formData.phone)) {
      newErrors.phone = t('addressForm.phoneInvalid');
    }

    // Address fields — English only validation
    if (!formData.address1.trim()) {
      newErrors.address1 = t('addressForm.streetAddressRequired');
    } else if (!ENGLISH_ONLY_PATTERN.test(formData.address1)) {
      newErrors.address1 = t('addressForm.englishOnly');
    }

    // City validation
    if (!formData.city.trim()) {
      newErrors.city = t('addressForm.cityRequired');
    } else if (!ENGLISH_ONLY_PATTERN.test(formData.city)) {
      newErrors.city = t('addressForm.englishOnly');
    }

    // State validation
    if (!formData.state.trim()) {
      newErrors.state = t('addressForm.stateRequired');
    } else if (!ENGLISH_ONLY_PATTERN.test(formData.state)) {
      newErrors.state = t('addressForm.englishOnly');
    }

    // Address2 validation (optional, but must be English if provided)
    if (formData.address2.trim() && !ENGLISH_ONLY_PATTERN.test(formData.address2)) {
      newErrors.address2 = t('addressForm.englishOnly');
    }

    // Postal code validation (US ZIP format)
    if (!formData.postalCode.trim()) {
      newErrors.postalCode = t('addressForm.postalCodeRequired');
    } else if (!/^[0-9]{5}(-[0-9]{4})?$/.test(formData.postalCode.trim())) {
      newErrors.postalCode = t('addressForm.postalCodeInvalid');
    }

    // Country is locked to US for now (no validation needed)
    // TODO: Re-enable country validation when multi-region shipping is supported
    // if (!formData.country) {
    //   newErrors.country = t('addressForm.countryRequired');
    // }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, t]);

  // ============ Event Handlers ============
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name as keyof AddressData]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      await onSave(formData);
    } catch (error) {
      console.error('Error saving address:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // ============ Korean Postal Code Lookup (disabled — US-only for now) ============
  // TODO: Re-enable when Korean shipping is supported
  // const handlePostalLookup = (): void => {
  //   if (typeof window !== 'undefined' && window.daum?.Postcode) {
  //     new window.daum.Postcode({
  //       oncomplete: (data: DaumPostcodeData) => {
  //         setFormData(prev => ({
  //           ...prev,
  //           postalCode: data.zonecode,
  //           address1: data.roadAddress || data.jibunAddress,
  //           state: data.sido,
  //           city: data.sigungu
  //         }));
  //         setErrors(prev => ({
  //           ...prev,
  //           postalCode: undefined,
  //           address1: undefined,
  //           state: undefined,
  //           city: undefined
  //         }));
  //       }
  //     }).open();
  //   } else {
  //     const script = document.createElement('script');
  //     script.src = '//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
  //     script.onload = () => {
  //       if (window.daum?.Postcode) {
  //         new window.daum.Postcode({
  //           oncomplete: (data: DaumPostcodeData) => {
  //             setFormData(prev => ({
  //               ...prev,
  //               postalCode: data.zonecode,
  //               address1: data.roadAddress || data.jibunAddress,
  //               state: data.sido,
  //               city: data.sigungu
  //             }));
  //             setErrors(prev => ({
  //               ...prev,
  //               postalCode: undefined,
  //               address1: undefined,
  //               state: undefined,
  //               city: undefined
  //             }));
  //           }
  //         }).open();
  //       }
  //     };
  //     document.head.appendChild(script);
  //   }
  // };

  // ============ Render ============
  if (!isOpen) return null;

  return (
    <div className="address-form-overlay">
      <div className="address-form-modal">
        <div className="address-form-header">
          <h2 className="address-form-title">
            {forSampleRequest
              ? t('addressForm.sampleTitle')
              : t('addressForm.shippingTitle')}
          </h2>
          <button
            type="button"
            className="address-form-close"
            onClick={onCancel}
            aria-label={t('addressForm.close')}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="address-form">
          {/* Recipient Name */}
          <div className="form-group">
            <label htmlFor="recipientName" className="form-label required">
              {t('addressForm.recipientName')}
            </label>
            <input
              type="text"
              id="recipientName"
              name="recipientName"
              value={formData.recipientName}
              onChange={handleChange}
              className={`form-input ${errors.recipientName ? 'form-input--error' : ''}`}
              placeholder={t('addressForm.recipientNamePlaceholder')}
            />
            {errors.recipientName && (
              <span className="form-error">{errors.recipientName}</span>
            )}
          </div>

          {/* Phone Number */}
          <div className="form-group">
            <label htmlFor="phone" className="form-label required">
              {t('addressForm.phone')}
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className={`form-input ${errors.phone ? 'form-input--error' : ''}`}
              placeholder={t('addressForm.phonePlaceholder')}
            />
            {errors.phone && (
              <span className="form-error">{errors.phone}</span>
            )}
          </div>

          {/* Country — soft-disabled to US only for now */}
          {/* TODO: Re-enable multi-country dropdown when international shipping is supported */}
          <div className="form-group">
            <label htmlFor="country" className="form-label required">
              {t('addressForm.country')}
            </label>
            <select
              id="country"
              name="country"
              value="US"
              className="form-input"
              disabled
              style={{ backgroundColor: '#f3f4f6', color: '#9ca3af', cursor: 'not-allowed' }}
            >
              <option value="US">{t('addressForm.countryUS')}</option>
              {/* Future countries (disabled for now):
              <option value="KR">{t('addressForm.countryKR')}</option>
              <option value="JP">{t('addressForm.countryJP')}</option>
              <option value="CN">{t('addressForm.countryCN')}</option>
              <option value="GB">{t('addressForm.countryGB')}</option>
              <option value="OTHER">{t('addressForm.countryOther')}</option>
              */}
            </select>
            <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
              {t('addressForm.usOnlyNotice')}
            </small>
          </div>

          {/* Postal Code */}
          <div className="form-group">
            <label htmlFor="postalCode" className="form-label required">
              {t('addressForm.postalCode')}
            </label>
            <div className="form-input-group">
              <input
                type="text"
                id="postalCode"
                name="postalCode"
                value={formData.postalCode}
                onChange={handleChange}
                className={`form-input ${errors.postalCode ? 'form-input--error' : ''}`}
                placeholder={t('addressForm.postalCodePlaceholder')}
                pattern="[0-9]{5}(-[0-9]{4})?"
                title={t('addressForm.postalCodeInvalid')}
              />
            </div>
            {errors.postalCode && (
              <span className="form-error">{errors.postalCode}</span>
            )}
          </div>

          {/* State/Province */}
          <div className="form-group">
            <label htmlFor="state" className="form-label required">
              {t('addressForm.state')}
            </label>
            <input
              type="text"
              id="state"
              name="state"
              value={formData.state}
              onChange={handleChange}
              className={`form-input ${errors.state ? 'form-input--error' : ''}`}
              placeholder={t('addressForm.statePlaceholder')}
            />
            {errors.state && (
              <span className="form-error">{errors.state}</span>
            )}
          </div>

          {/* City */}
          <div className="form-group">
            <label htmlFor="city" className="form-label required">
              {t('addressForm.city')}
            </label>
            <input
              type="text"
              id="city"
              name="city"
              value={formData.city}
              onChange={handleChange}
              className={`form-input ${errors.city ? 'form-input--error' : ''}`}
              placeholder={t('addressForm.cityPlaceholder')}
            />
            {errors.city && (
              <span className="form-error">{errors.city}</span>
            )}
          </div>

          {/* Street Address */}
          <div className="form-group">
            <label htmlFor="address1" className="form-label required">
              {t('addressForm.streetAddress')}
            </label>
            <input
              type="text"
              id="address1"
              name="address1"
              value={formData.address1}
              onChange={handleChange}
              className={`form-input ${errors.address1 ? 'form-input--error' : ''}`}
              placeholder={t('addressForm.streetAddressPlaceholder')}
            />
            {errors.address1 && (
              <span className="form-error">{errors.address1}</span>
            )}
          </div>

          {/* Detailed Address (Apartment, Unit, etc.) */}
          <div className="form-group">
            <label htmlFor="address2" className="form-label">
              {t('addressForm.detailAddress')}
            </label>
            <input
              type="text"
              id="address2"
              name="address2"
              value={formData.address2}
              onChange={handleChange}
              className={`form-input ${errors.address2 ? 'form-input--error' : ''}`}
              placeholder={t('addressForm.detailAddressPlaceholder')}
            />
            {errors.address2 && (
              <span className="form-error">{errors.address2}</span>
            )}
          </div>

          {/* Form Actions */}
          <div className="address-form-actions">
            <button
              type="button"
              onClick={onCancel}
              className="form-button form-button-cancel"
              disabled={isSaving}
            >
              {t('addressForm.cancel')}
            </button>
            <button
              type="submit"
              className="form-button form-button-primary"
              disabled={isSaving}
            >
              {isSaving ? t('addressForm.saving') : t('addressForm.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddressForm;
